import moment from 'moment';
import holidays from '../settings/holidays';

/**
 * Determines whether a {@link moment.Moment} instance is a business day for the selected holiday set.
 *
 * @param date Moment instance to evaluate.
 * @param holidaySet Set of holiday dates in `YYYY-MM-DD` format.
 * @returns True when the date is a weekday and not a configured holiday.
 */
function isBusinessDay(
	date: moment.Moment,
	holidaySet: Set<string>,
): boolean {
	const isoWeekday = date.isoWeekday();

	if (
		isoWeekday < 1
		|| isoWeekday > 5
	) {
		return false;
	}

	return !holidaySet.has(date.format('YYYY-MM-DD'));
}

/**
 * Builds a normalized holiday set for a country code.
 *
 * @param country Country code used by the creator settings.
 * @returns Set of holiday date strings.
 */
function getCountryHolidaySet(country: string): Set<string> {
	const normalizedCountry = country.toLowerCase();
	const countryHolidays = holidays[normalizedCountry];

	if (!countryHolidays) {
		return new Set<string>();
	}

	return new Set<string>(countryHolidays);
}

const compileBusinessDays = (
	date: string | null,
	days: number,
	country: string,
	format?: string,
) => {
	const dateFormat = format || 'YYYY-MM-DD HH:mm:ss';
	const holidaySet = getCountryHolidaySet(country);
	const workingDate = (
		date
			? moment(date)
			: moment()
	);

	if (!workingDate.isValid()) {
		return workingDate.format(dateFormat);
	}

	const roundedDays = Number.isFinite(days)
		? Math.round(days)
		: 0;

	if (roundedDays === 0) {
		return workingDate.format(dateFormat);
	}

	const direction = roundedDays < 0
		? -1
		: 1;
	let remaining = Math.abs(roundedDays);

	while (remaining > 0) {
		workingDate.add(
			direction,
			'days',
		);

		if (isBusinessDay(
			workingDate,
			holidaySet,
		)) {
			remaining -= 1;
		}
	}

	return workingDate.format(dateFormat);
};

export default compileBusinessDays;
